import { Metadata, Pagination, Quote } from '@deliveries-app/models';
import api from '../../api';
import {
  QuoteResponse,
  DetailQuoteResponse,
} from '../models/quote-response.interface';
import {
  GetAllQuotesFilters,
  QuotePayload,
} from '../models/quote-request.interface';
import { processFilterParams } from '../utils/filterParams';

type Message = {
  message: string;
};

/**
 * Get all quotes by branch id
 *
 * @param {number} branch_id
 *
 * @returns {Promise<Quote[]>}
 */
export async function getAllQuotes(
  branch_id: string | number,
  filters?: GetAllQuotesFilters
): Promise<{
  data: Quote[];
  _metadata: Metadata;
}> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });

    const response = await api.get<QuoteResponse>(
      `/api/v1/branches/${branch_id}/quotes`,
      {
        params,
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('getAllQuote failed', error);
    throw error.message;
  }
}

/**
 * Get a single quote by id and branch id
 *
 * @param {number} branch_id
 * @param {number} id
 *
 * @returns {Promise<Quote>}
 */
export async function getQuote(
  branch_id: string | number,
  id: number
): Promise<Quote> {
  try {
    const response = await api.get<DetailQuoteResponse>(
      `/api/v1/branches/${branch_id}/quotes/${id}`
    );

    return response.data.data;
  } catch (error: any) {
    console.error('getQuote failed', error);

    throw error;
  }
}

export async function createQuote(
  branch_id: string | number,
  payload: QuotePayload
): Promise<Quote> {
  try {
    const response = await api.post<DetailQuoteResponse>(
      `/api/v1/branches/${branch_id}/quotes/create`,
      payload
    );

    return response.data.data;
  } catch (error: any) {
    console.error('failed create quotes!', error);

    if (error.response?.data) {
      let values: string[] = [];

      if (error.response.data.errors) {
        const errors = error.response.data.errors;

        Object.entries(errors)
          .map(([key, value]) => value as string[])
          .forEach((value: string[]) => {
            console.log(...value);
            values = [...values, ...value];
          });
      }

      throw {
        error: error.response.data.message,
        errors: values,
      };
    }

    throw error;
  }
}

export async function updateQuote(
  branch_id: string | number,
  payload: QuotePayload,
  id: number
): Promise<Quote> {
  try {
    const response = await api.patch(
      `/api/v1/branches/${branch_id}/quotes/${id}`,
      payload
    );

    return response.data.data;
  } catch (error) {
    console.error('failed update quotes!', error);
    throw error;
  }
}

export async function deleteQuote(
  branch_id: string | number,
  id: number
): Promise<Message> {
  try {
    const response = await api.delete(
      `/api/v1/branches/${branch_id}/quotes/${id}`
    );

    return response.data.message;
  } catch (error) {
    console.error('failed to delete quotes!', error);
    throw error;
  }
}

export async function getQuotePdfUrl(
  branch_id: string | number,
  id: number
): Promise<string> {
  try {
    const response = await api.get<{
      url: string;
    }>(`/api/v1/branches/${branch_id}/quotes/${id}/pdf`);
    return response.data.url;
  } catch (error) {
    console.error('failed to get pdf quotes!', error);
    throw error;
  }
}

export async function sendPDFToCustomer(
  branch_id: string | number,
  id: number
): Promise<any> {
  try {
    const response = await api.post(
      `/api/v1/branches/${branch_id}/quotes/${id}/email`
    );
    return response.data;
  } catch (error) {
    console.error('failed to get pdf quotes!', error);
    throw error;
  }
}

export async function approveQuotes(
  branch_id: string | number,
  id: number,
  dateTime: Date | string,
  vehicleId: number
): Promise<any> {
  try {
    const response = await api.get(
      `/api/v1/branches/${branch_id}/quotes/${id}/email`,
      {
        params: {
          dateTime,
          vehicleId,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('failed to approve pdf quotes!', error);
    throw error;
  }
}

export async function generateQuote(
  branch_id: number | string,
  id: number
): Promise<any> {
  try {
    const response = await api.patch(
      `/api/v1/branches/${branch_id}/quotes/${id}/generate`
    );

    return response;
  } catch (error) {
    console.error('failed to approve pdf quotes!', error);
    throw error;
  }
}

export const quoteService = {
  getAllQuotes,
  approveQuotes,
  createQuote,
  deleteQuote,
  getQuote,
  getQuotePdfUrl,
  sendPDFToCustomer,
  updateQuote,
  generateQuote,
};
